import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Words, FadeUp, PopIn, EASE } from "@/components/Reveal";
import PillButton from "@/components/PillButton";
import EpisodeCard from "@/components/EpisodeCard";
import { EPISODES } from "@/data/episodes";
import { CAUSES } from "@/data/causes";
import { PLATFORMS, QUOTE } from "@/data/site";

const NOISE_MASK = {
  WebkitMaskImage: "url('/figma/pattern-noise.png')",
  maskImage: "url('/figma/pattern-noise.png')",
  WebkitMaskRepeat: "repeat",
  maskRepeat: "repeat",
  WebkitMaskSize: "360px 360px",
  maskSize: "360px 360px",
};

const PlatformIcon = ({ icon }) => {
  const props = { viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, className: "h-[14px] w-[14px] sm:h-4 sm:w-4" };
  if (icon === "spotify")
    return (
      <svg {...props}>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.3" />
        <path d="M7.5 9.5c3-1 6-1 9 .5M7.5 12.5c2.5-.8 5-.8 7.5.5M8 15.5c2-.6 4-.6 6 .5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    );
  if (icon === "apple")
    return (
      <svg {...props}>
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.3" />
        <path d="M12 6v10M9 10.5v3M15 10.5v3M6.5 12.5c-.3 1 .1 2 1 2.4M17.5 12.5c.3 1-.1 2-1 2.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      </svg>
    );
  if (icon === "youtube")
    return (
      <svg {...props}>
        <path d="M21.6 7.2a2.5 2.5 0 0 0-1.76-1.77C18.27 5 12 5 12 5s-6.27 0-7.84.43A2.5 2.5 0 0 0 2.4 7.2 26 26 0 0 0 2 12a26 26 0 0 0 .4 4.8 2.5 2.5 0 0 0 1.76 1.77C5.73 19 12 19 12 19s6.27 0 7.84-.43A2.5 2.5 0 0 0 21.6 16.8 26 26 0 0 0 22 12a26 26 0 0 0-.4-4.8z" stroke="currentColor" strokeWidth="1.3" />
        <path d="M10 9v6l5-3-5-3z" fill="currentColor" />
      </svg>
    );
  return (
    <svg {...props}>
      <path d="M4 12a8 8 0 0 1 8 8M4 6a14 14 0 0 1 14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="5.5" cy="18.5" r="1.6" fill="currentColor" />
    </svg>
  );
};

const LockIcon = ({ className = "h-3 w-3" }) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className={className}>
    <rect x="5" y="10.5" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="12" cy="15" r="1.15" fill="currentColor" />
  </svg>
);

const Latest = EPISODES[0];
const carouselEpisodes = EPISODES.slice(0, 5);

const Home = () => {
  const heroRef = useRef(null);
  const swiperRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 110]);

  return (
    <main>
      {/* ============ HERO ============ */}
      <section id="top" ref={heroRef} className="relative isolate w-full overflow-hidden bg-white" style={{ backgroundColor: "rgb(255,255,255)" }}>
        <div className="relative h-[624px] w-full sm:h-[692px] md:h-[756px] lg:h-[796px] xl:h-[836px]">
          <div className="absolute inset-0 bg-white" style={{ clipPath: "inset(0% 8.36% 0% 0%)" }}>
            <div className="absolute inset-x-0 bottom-0 top-0 bg-white lg:top-[76px]">
              <motion.div style={{ y: heroY }} className="absolute inset-0">
                <img
                  alt="Stephanie Roxanne in her studio, mid-conversation on the couch with a microphone"
                  className="h-full w-full object-cover object-[70%_50%] sm:object-[68%_50%] md:object-[65%_50%] lg:object-[60%_top] xl:object-[55%_top] 2xl:object-[50%_top]"
                  src="/figma/hero.jpg"
                />
              </motion.div>
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 hidden lg:block"
                style={{ background: "linear-gradient(90deg, rgb(255,255,255) 0%, rgba(255,255,255,0.94) 12%, rgba(255,255,255,0.4) 42%, rgba(255,255,255,0) 72%)" }}
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 lg:hidden"
                style={{ background: "linear-gradient(rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.8) 30%, rgba(255,255,255,0.55) 55%, rgba(255,255,255,0.15) 85%, rgba(255,255,255,0) 100%)" }}
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 bottom-0 h-24"
                style={{ background: "linear-gradient(rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 65%, rgb(255,255,255) 100%)" }}
              />
            </div>
          </div>

          <div className="relative mx-auto h-full w-full max-w-[1440px] pt-[64px] sm:pt-[72px] lg:pt-[76px]">
            <div className="flex h-full max-w-[640px] flex-col justify-start px-5 pt-10 sm:max-w-[720px] sm:px-8 sm:pt-14 lg:mx-0 lg:max-w-[540px] lg:pl-[56px] lg:pr-0 lg:pt-[110px] xl:pl-[80px] xl:pt-[128px]">
              <Words text="Listening Beyond Words." mode="load" delay={0.15} stagger={0.08} className="eyebrow inline-flex overflow-hidden text-[12px] leading-[22.4px] text-charcoal sm:text-[13px] lg:text-[14px]" />
              <h1
                className="mt-3 font-display text-[40px] leading-[1.08] text-black sm:text-[54px] sm:leading-[1.06] md:text-[60px] lg:mt-4 lg:text-[68px] lg:leading-[74px] xl:text-[74px] xl:leading-[80px]"
                aria-label="Faith, Spoken Out Loud."
              >
                <span className="block">
                  <Words text="Faith, Spoken" mode="load" delay={0.25} stagger={0.09} />
                </span>
                <span className="block">
                  <Words text="Out Loud." mode="load" delay={0.45} stagger={0.09} />
                </span>
              </h1>
              <FadeUp mode="load" delay={0.7} y={16} className="mt-4 max-w-[500px] text-[15px] leading-[26px] text-charcoal sm:text-[16px] sm:leading-[27px] lg:mt-5 lg:text-[16.3px] lg:leading-[28.8px]">
                <p>
                  Stephanie Roxanne hosts honest conversations rooted in faith. Real testimonies, honest talks about Jesus, politics, and culture, and the God who
                  holds it all together.
                </p>
              </FadeUp>
              <FadeUp mode="load" delay={0.85} y={14} className="mt-6 flex flex-col items-start gap-3 sm:mt-7 sm:flex-row sm:items-center sm:gap-4 lg:mt-8">
                <PillButton href="/episodes" dataTestId="hero-listen-button">
                  Listen to the Podcast
                </PillButton>
                <PillButton href="/about" variant="outline" dataTestId="hero-meet-button">
                  Meet Stephanie
                </PillButton>
              </FadeUp>
            </div>
          </div>
        </div>

        {/* ============ LATEST EPISODE CARD ============ */}
        <div className="relative w-full pb-14 sm:pb-16 lg:pb-[85px]">
          <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-[135px]">
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 1, ease: EASE }}
              className="relative -mt-14 sm:-mt-16 lg:-mt-[92px]"
            >
              <article
                data-testid="latest-episode-card"
                aria-label={`Latest episode: ${Latest.title}`}
                className="group relative overflow-hidden rounded-2xl border border-hairline bg-white shadow-[0_10px_40px_-24px_rgba(78,81,85,0.28)] transition-shadow duration-500 hover:shadow-[0_28px_70px_-32px_rgba(78,81,85,0.4)]"
              >
                <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_340px] lg:grid-cols-[minmax(0,1fr)_400px]">
                  <div className="order-2 flex flex-col gap-6 px-6 pt-6 pb-6 md:order-1 md:gap-7 md:px-8 md:pt-8 md:pb-8 lg:px-10 lg:pt-10 lg:pb-10">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
                      <PopIn delay={0.1} className="inline-flex">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-silver bg-white px-3 py-1 text-[11px] uppercase leading-[16px] tracking-[0.22em] text-charcoal sm:text-[11.5px] font-display">
                          <RadioGlyph /> Latest Episode
                        </span>
                      </PopIn>
                      <PopIn delay={0.2} className="inline-flex">
                        <span className="inline-flex items-center rounded-full bg-linen px-3 py-1 text-[11px] uppercase leading-[16px] tracking-[0.22em] text-black sm:text-[11.5px] font-display">
                          {Latest.track}
                        </span>
                      </PopIn>
                      <PopIn delay={0.3} className="inline-flex">
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-hairline bg-white-smoke px-3 py-1 text-[11px] uppercase leading-[16px] tracking-[0.22em] text-charcoal-mid sm:text-[11.5px] font-display" title="This episode isn't live yet">
                          <LockIcon /> Coming Soon
                        </span>
                      </PopIn>
                    </div>
                    <Words text={Latest.title} className="block font-display text-[30px] leading-[1.08] text-black sm:text-[38px] lg:text-[44px] lg:leading-[50px] xl:text-[48px] xl:leading-[54px]" />
                    <FadeUp delay={0.1}>
                      <p className="max-w-[560px] text-[14.5px] leading-[24px] text-charcoal sm:text-[15px] sm:leading-[25.6px]">{Latest.description}</p>
                    </FadeUp>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4 border-t border-hairline pt-5 sm:grid-cols-4 sm:gap-x-4">
                      {[
                        ["Guest", "Open conversation"],
                        ["Release", Latest.date],
                        ["Runtime", Latest.duration],
                        ["Track", Latest.track],
                      ].map(([k, v], i) => (
                        <FadeUp key={k} delay={0.06 * i} y={10}>
                          <div className="flex flex-col gap-1">
                            <span className="eyebrow text-[10.5px] leading-[16px] text-charcoal-mid">{k}</span>
                            <span className="font-display text-[14px] leading-[20px] text-black sm:text-[14.5px]">{v}</span>
                          </div>
                        </FadeUp>
                      ))}
                    </div>
                    <FadeUp y={12} className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-3">
                      <PillButton href="/community" dataTestId="latest-notify-button" className="w-full sm:w-auto">
                        Notify Me
                      </PillButton>
                      <PillButton href="/episodes/ep-09" variant="outline" dataTestId="latest-shownotes-button" className="w-full sm:w-auto">
                        View Show Notes
                      </PillButton>
                    </FadeUp>
                  </div>
                  <div className="order-1 relative aspect-square w-full overflow-hidden bg-white-smoke md:order-2 md:aspect-auto md:h-full md:min-h-[380px]">
                    <div className="absolute inset-0">
                      <img alt="Warm studio microphone" className="h-full w-full object-cover" src="/figma/player-art.png" />
                    </div>
                    <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(rgba(8,9,10,0) 55%, rgba(8,9,10,0.35) 100%)" }} />
                    <div className="absolute right-4 top-4 flex items-center gap-2">
                      <span aria-hidden="true" title="Audio coming soon" className="grid h-11 w-11 place-items-center rounded-full border border-white/40 bg-white/15 text-linen backdrop-blur-sm">
                        <LockIcon className="h-4 w-4" />
                      </span>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 px-5 py-4">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1 text-[10.5px] uppercase leading-[16px] tracking-[0.22em] text-linen backdrop-blur-sm sm:text-[11px] font-display">
                        <LockIcon /> Audio coming soon
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-3 border-t border-hairline bg-white-smoke/50 px-6 py-4 md:flex-row md:items-center md:justify-between md:px-8 md:py-5 lg:px-10">
                  <FadeUp y={8}>
                    <span className="eyebrow block text-[10.5px] leading-[16px] text-charcoal-mid sm:text-[11px]">Listen soon on</span>
                  </FadeUp>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-2.5">
                    {PLATFORMS.map((p, i) => (
                      <PopIn key={p.label} delay={0.06 * i} className="inline-flex">
                        <span
                          role="img"
                          aria-label={`${p.label} — coming soon`}
                          title={`${p.label} — coming soon`}
                          className="inline-flex items-center gap-2 rounded-full border border-hairline bg-white px-3.5 py-1.5 text-[12.5px] leading-[20px] text-charcoal-mid transition-colors duration-500 hover:border-silver hover:text-charcoal sm:px-4 sm:py-2 sm:text-[13px]"
                        >
                          <PlatformIcon icon={p.icon} />
                          <span className="font-display uppercase tracking-[0.14em]">{p.label}</span>
                        </span>
                      </PopIn>
                    ))}
                  </div>
                  <FadeUp y={8} className="hidden md:block">
                    <Link to="/episodes" data-testid="latest-episode-library-link" className="group/link inline-flex items-center gap-1 font-display text-[13px] leading-[22px] text-charcoal transition-colors hover:text-black sm:text-[13.5px]">
                      <span>Episode library</span>
                      <span aria-hidden="true" className="transition-transform duration-500 ease-out group-hover/link:translate-x-0.5">
                        <ArrowRight />
                      </span>
                    </Link>
                  </FadeUp>
                </div>
              </article>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ ABOUT ============ */}
      <section id="about" className="w-full bg-white-smoke">
        <div className="mx-auto max-w-[1440px] px-5 py-16 sm:px-8 sm:py-20 lg:px-[56px] lg:py-[80px]">
          <div className="mx-auto grid max-w-[1208px] grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-[56px] lg:gap-[72px] lg:px-[60px]">
            <div className="flex flex-col gap-6 lg:gap-8">
              <div className="flex flex-col gap-2">
                <FadeUp y={14}>
                  <span className="eyebrow block text-[13px] leading-[22.4px] text-black sm:text-[14px]">About Stephanie</span>
                </FadeUp>
                <h2 className="font-display text-[32px] leading-[1.1] text-black sm:text-[38px] lg:text-[44px] lg:leading-[49.28px]" aria-label="A Relatable Voice Who Loves Jesus">
                  <span className="block">
                    <Words text="A Relatable Voice Who" />
                  </span>
                  <span className="block">
                    <Words text="Loves Jesus" delay={0.15} />
                  </span>
                </h2>
              </div>
              <FadeUp y={18}>
                <p className="max-w-[500px] text-[15.5px] leading-[27px] text-black sm:text-[16.2px] sm:leading-[28.8px]">
                  She loves Jesus and says so naturally — no polish, no pulpit, just
                  honest conversations from a couch with a microphone.
                </p>
              </FadeUp>
              <div className="flex flex-col gap-3 lg:gap-2">
                <FadeUp y={16}>
                  <p className="max-w-[481px] text-[14.4px] leading-[25px] text-charcoal sm:text-[14.6px] sm:leading-[25.6px]">
                    Stephanie Roxanne is a Colorado-based podcast host, wife, mom, and follower of Jesus. Over the years she’s been involved with pro-life
                    organizations, constitutional education, and community outreach. Those experiences shape the conversations she has today, but at the center
                    of it all is her faith.
                  </p>
                </FadeUp>
                <FadeUp y={16}>
                  <p className="max-w-[481px] text-[14.3px] leading-[25px] text-charcoal sm:text-[14.5px] sm:leading-[25.6px]">
                    The podcast features two series: Testimony Tuesday, real people telling what God has done in their lives, and open conversations on Jesus,
                    politics, and culture. Curious, warm, and never watered down.
                  </p>
                </FadeUp>
                <FadeUp y={20}>
                  <blockquote className="relative mt-2 pl-5">
                    <span aria-hidden="true" className="absolute left-0 top-0 h-full w-[2px] origin-top bg-silver" />
                    <p className="font-display text-[18px] leading-[27px] text-black sm:text-[20px] sm:leading-[29px]">{QUOTE.text}</p>
                    <cite className="mt-2 block not-italic text-[12.9px] leading-[22.4px] text-charcoal">— {QUOTE.author}</cite>
                  </blockquote>
                </FadeUp>
              </div>
              <FadeUp y={14}>
                <PillButton href="/about" variant="outline" dataTestId="home-about-read-button">
                  Read the Full Story
                </PillButton>
              </FadeUp>
            </div>
            <FadeUp y={30} className="group relative mx-auto aspect-[568/676] w-full max-w-[568px] overflow-hidden rounded-2xl md:mx-0">
              <div className="absolute inset-0 transition-transform duration-[1200ms] group-hover:scale-[1.04]" style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}>
                <img alt="A warm podcast studio — microphone and headphones ready for conversation" className="h-full w-full object-cover" src="/figma/about-media-overlay.jpg" />
              </div>
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-linen opacity-0 mix-blend-multiply transition-opacity duration-500 group-hover:opacity-15" />
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ============ EPISODES CAROUSEL ============ */}
      <section id="episodes" className="w-full bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-16 sm:px-8 sm:py-20 lg:px-[56px] lg:py-[80px]">
          <div className="mx-auto flex max-w-[1208px] flex-col gap-8 lg:gap-10 lg:px-[60px]">
            <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
              <div className="flex w-full max-w-[524px] flex-col">
                <FadeUp y={14}>
                  <span className="eyebrow block text-[13px] leading-[22.4px] text-charcoal sm:text-[14px]">The Podcast</span>
                </FadeUp>
                <h2 className="mt-3 font-display text-[32px] leading-[1.1] text-black sm:text-[38px] lg:text-[44px] lg:leading-[49.28px]" aria-label="Browse the Conversations">
                  <Words text="Browse the Conversations" />
                </h2>
                <FadeUp y={18}>
                  <p className="mt-3 text-[14.6px] leading-[26px] text-charcoal sm:text-[15.3px] sm:leading-[27.2px]">
                    Here are the latest episodes from both podcast series. Want to hear more? Browse the full library.
                  </p>
                </FadeUp>
                <FadeUp y={12}>
                  <div className="mt-4">
                    <Link to="/episodes" data-testid="home-episodes-library-link" className="group/link inline-flex items-center font-display text-[15px] leading-[25.6px] text-charcoal sm:text-[16px]">
                      <span className="transition-transform duration-500 ease-out group-hover/link:-translate-x-1">See the full library</span>
                      <span aria-hidden="true" className="ml-1 transition-transform duration-500 ease-out group-hover/link:translate-x-1">→</span>
                    </Link>
                  </div>
                </FadeUp>
              </div>
              <FadeUp y={10} className="flex items-center gap-3">
                <button
                  type="button"
                  aria-label="Previous episodes"
                  data-testid="episodes-carousel-prev"
                  onClick={() => swiperRef.current?.scrollBy({ left: -swiperRef.current.clientWidth, behavior: "smooth" })}
                  className="grid h-11 w-11 place-items-center rounded-full border border-black text-black transition-all duration-300 hover:bg-black hover:text-linen"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  type="button"
                  aria-label="Next episodes"
                  data-testid="episodes-carousel-next"
                  onClick={() => swiperRef.current?.scrollBy({ left: swiperRef.current.clientWidth, behavior: "smooth" })}
                  className="grid h-11 w-11 place-items-center rounded-full border border-black text-black transition-all duration-300 hover:bg-black hover:text-linen"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </FadeUp>
            </div>
            <div
              ref={swiperRef}
              data-testid="episodes-carousel"
              className="no-scrollbar -mx-5 flex snap-x snap-mandatory gap-7 overflow-x-auto scroll-smooth px-5 sm:mx-0 sm:px-0"
            >
              {carouselEpisodes.map((ep, i) => (
                <div key={ep.id} className="w-[85%] shrink-0 snap-start sm:w-[calc(50%-14px)] lg:w-[calc((100%-56px)/3)]">
                  <EpisodeCard episode={ep} index={i} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ CAUSES ============ */}
      <section id="causes" className="relative w-full bg-white-smoke">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-silver"
          style={{ ...NOISE_MASK, opacity: 0.14 }}
        />
        <div className="relative mx-auto max-w-[1440px] px-5 py-16 sm:px-8 sm:py-20 lg:px-[56px] lg:py-[80px]">
          <div className="mx-auto flex max-w-[1208px] flex-col gap-8 lg:px-[60px]">
            <div>
              <FadeUp y={14}>
                <span className="eyebrow block text-[13px] leading-[22.4px] text-charcoal sm:text-[14px]">Causes &amp; Affiliations</span>
              </FadeUp>
              <h2 className="mt-3 font-display text-[32px] leading-[1.1] text-black sm:text-[38px] lg:text-[44px] lg:leading-[49.28px]" aria-label="The Issues She Comes Back To">
                <Words text="The Issues She Comes Back To" stagger={0.06} />
              </h2>
              <FadeUp y={18}>
                <p className="mt-4 max-w-[550px] text-[14.6px] leading-[26px] text-charcoal sm:text-[15.3px] sm:leading-[27.2px]">
                  Stephanie believes faith should be lived out, not just talked about. These are some of the organizations she proudly supports.
                </p>
              </FadeUp>
            </div>
            <div className="flex flex-col divide-y divide-silver-line">
              {CAUSES.map((cause, i) => (
                <div key={cause.slug} className={i === 0 ? "pb-8 md:pb-10" : "py-8 md:py-10"}>
                  <Link
                    to={`/causes/${cause.slug}`}
                    data-testid={`home-cause-link-${cause.slug}`}
                    aria-label={`Open issue: ${cause.headline}`}
                    className="group grid grid-cols-1 items-center gap-8 rounded-2xl px-2 py-2 transition-colors duration-500 md:grid-cols-[1fr_340px] md:gap-10 md:px-4"
                  >
                    <div className="flex flex-col gap-3">
                      <h3 className="font-display text-[26px] leading-[1.15] text-black transition-transform duration-500 ease-out group-hover:-translate-x-[2px] sm:text-[28px] lg:text-[31px] lg:leading-[37.2px]">
                        <Words text={cause.headline} stagger={0.035} />
                      </h3>
                      <PopIn>
                        <span className="inline-flex w-fit items-center rounded-full border border-silver px-4 py-[3px] text-[13px] uppercase leading-[22.4px] tracking-[0.08em] text-charcoal transition-colors group-hover:border-black group-hover:bg-linen sm:text-[14px] font-display">
                          {cause.tag}
                        </span>
                      </PopIn>
                      <FadeUp y={16}>
                        <p className="max-w-[625px] text-[14.4px] leading-[25.6px] text-charcoal sm:text-[14.6px]">{cause.description}</p>
                      </FadeUp>
                      <FadeUp y={12}>
                        <span className="mt-1 inline-flex items-center font-display text-[15px] leading-[25.6px] text-charcoal sm:text-[16px]">
                          <span className="transition-transform duration-500 ease-out group-hover:-translate-x-[2px]">Read the full brief</span>
                          <span aria-hidden="true" className="ml-1 transition-transform duration-500 ease-out group-hover:translate-x-1">→</span>
                        </span>
                      </FadeUp>
                    </div>
                    <motion.div
                      initial={{ clipPath: i % 2 === 0 ? "inset(0% 0% 0% 100%)" : "inset(0% 100% 0% 0%)" }}
                      whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ duration: 1, ease: EASE }}
                      className="relative block aspect-[340/255] w-full overflow-hidden rounded-2xl"
                    >
                      <div className="absolute inset-0 transition-transform duration-[1100ms] group-hover:scale-[1.06]" style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}>
                        <img alt={cause.imageAlt} className="h-full w-full object-cover" src={cause.image} />
                      </div>
                      <div className="pointer-events-none absolute inset-0 bg-linen opacity-15 mix-blend-multiply transition-opacity duration-500 group-hover:opacity-0" />
                    </motion.div>
                  </Link>
                </div>
              ))}
            </div>
            <FadeUp y={12}>
              <Link to="/causes" data-testid="home-causes-all-link" className="group inline-flex items-center font-display text-[15px] leading-[25.6px] text-charcoal sm:text-[16px]">
                <span className="transition-transform duration-500 ease-out group-hover:-translate-x-1">See every issue and every partner</span>
                <span aria-hidden="true" className="ml-1 transition-transform duration-500 ease-out group-hover:translate-x-1">→</span>
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ============ COMMUNITY ============ */}
      <section id="community" className="w-full bg-white">
        <div className="mx-auto max-w-[1440px] px-5 py-12 sm:px-8 sm:py-20 lg:px-[56px] lg:py-[80px]">
          <div className="mx-auto grid max-w-[1208px] grid-cols-1 items-center gap-10 md:grid-cols-2 md:gap-14 lg:gap-16 lg:px-[60px]">
            <div className="flex flex-col">
              <PopIn>
                <span className="inline-flex w-fit items-center rounded-full bg-linen px-[18px] py-1 text-[13px] uppercase leading-[22.4px] tracking-[0.1em] text-black sm:text-[14px] font-display">
                  Join
                </span>
              </PopIn>
              <h2 className="mt-3 font-display text-[32px] leading-[1.1] text-black sm:text-[36px] lg:mt-[13px] lg:text-[42px] lg:leading-[47px]" aria-label="Come Sit With Us">
                <Words text="Come Sit With Us" />
              </h2>
              <FadeUp y={18}>
                <p className="mt-3 max-w-[432px] text-[14.6px] leading-[26px] text-charcoal sm:text-[15.4px] sm:leading-[27.2px] lg:mt-[13px]">
                  Be part of Stephanie's community for occasional updates that matter —
                  no newsletter, no noise, nothing sold.
                </p>
              </FadeUp>
              <FadeUp y={14}>
                <div className="mt-6 lg:mt-[26px]">
                  <PillButton href="/community" dataTestId="home-join-button">
                    Join the Community
                  </PillButton>
                </div>
              </FadeUp>
              <FadeUp y={8}>
                <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12.9px] leading-[22.4px] text-charcoal sm:text-[13px] lg:mt-[18px]">
                  {["One community, no spam", "Occasional updates", "Unsubscribe any time"].map((t) => (
                    <span key={t} className="flex items-center gap-1">
                      <span className="text-[14px] font-semibold leading-[22.4px] text-charcoal-dark">✓</span> {t}
                    </span>
                  ))}
                </div>
              </FadeUp>
            </div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.9, ease: EASE }}
              className="group relative aspect-[572/334] w-full overflow-hidden rounded-3xl"
            >
              <div className="absolute inset-0 transition-transform duration-[1200ms] group-hover:scale-[1.05]" style={{ transitionTimingFunction: "cubic-bezier(0.22,1,0.36,1)" }}>
                <img alt="Friends gathered around a table in warm light" className="h-full w-full object-cover" src="/figma/community-photo.jpg" />
              </div>
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-linen opacity-0 mix-blend-multiply transition-opacity duration-500 group-hover:opacity-15" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ NORMALIZING GOD ============ */}
      <section id="podcast-site" className="relative w-full overflow-hidden bg-ink text-linen">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(1200px 500px at 20% 0%, rgba(245,233,224,0.14), transparent 60%), radial-gradient(900px 420px at 100% 100%, rgba(245,233,224,0.1), transparent 55%)" }}
        />
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-linen" style={{ ...NOISE_MASK, opacity: 0.1 }} />
        <div className="relative overflow-hidden border-b border-linen/10 py-4" data-testid="home-marquee">
          <div className="animate-marquee flex w-max items-center gap-8 whitespace-nowrap">
            {[0, 1].map((dup) => (
              <div key={dup} className="flex items-center gap-8" aria-hidden={dup === 1}>
                {["Normalizing God", "Faith, Spoken Out Loud", "Testimony Tuesday", "Listening Beyond Words", "Faith & Politics"].map((t) => (
                  <span key={`${dup}-${t}`} className="flex items-center gap-8 font-display text-[13px] uppercase tracking-[0.3em] text-linen/40">
                    {t}
                    <span className="h-1 w-1 rounded-full bg-linen/40" aria-hidden="true" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="relative mx-auto max-w-[1440px] px-5 py-16 sm:px-8 sm:py-20 lg:px-[56px] lg:py-[96px]">
          <div className="mx-auto grid max-w-[1208px] grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] lg:gap-16 lg:px-[60px]">
            <div className="flex flex-col items-start gap-5">
              <PopIn>
                <span className="inline-flex w-fit items-center rounded-full bg-linen/10 px-4 py-1 text-[12.5px] uppercase leading-[22.4px] tracking-[0.22em] text-linen backdrop-blur-sm sm:text-[13px] font-display">
                  Normalizing God
                </span>
              </PopIn>
              <h2 className="max-w-[720px] font-display text-[30px] leading-[1.1] text-linen sm:text-[38px] lg:text-[48px] lg:leading-[56px]" aria-label="Make Talking About Jesus Ordinary Again">
                <Words text="Make Talking About Jesus" stagger={0.06} />
                <span className="block">
                  <Words text="Ordinary Again" delay={0.2} stagger={0.06} />
                </span>
              </h2>
              <FadeUp y={16}>
                <p className="max-w-[560px] text-[15px] leading-[27px] text-linen/80 sm:text-[16px] sm:leading-[28.8px]">
                  The companion podcast built on one conviction: conversation about God belongs in ordinary rooms. Full episodes, honest exchanges, and the
                  quotes worth carrying into the workplace, the school, and the neighborhood.
                </p>
              </FadeUp>
              <FadeUp y={14}>
                <span
                  aria-disabled="true"
                  role="button"
                  title="Normalizing God launches soon"
                  data-testid="normalizing-god-coming-soon"
                  className="inline-flex cursor-not-allowed select-none items-center gap-2 rounded-full border border-linen/40 bg-transparent px-8 py-[9px] text-[15px] uppercase leading-[25.6px] tracking-[0.08em] text-linen/80 sm:text-[16px] font-display"
                >
                  <LockIcon className="h-4 w-4" />
                  <span>Visit — Coming Soon</span>
                </span>
              </FadeUp>
            </div>
            <FadeUp y={30} className="relative mx-auto aspect-square w-full max-w-[380px] lg:mx-0 lg:justify-self-end">
              <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-full border border-linen/12" />
              <span aria-hidden="true" className="pointer-events-none absolute inset-6 rounded-full border border-linen/18" />
              <span aria-hidden="true" className="pointer-events-none absolute inset-14 rounded-full border border-linen/25" />
              <motion.span
                aria-hidden="true"
                className="pointer-events-none absolute inset-14 rounded-full"
                style={{ background: "radial-gradient(circle, rgba(245,233,224,0.28) 0%, rgba(245,233,224,0) 65%)" }}
                animate={{ opacity: [0.35, 0.7, 0.35], scale: [0.97, 1, 0.97] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              />
              <div className="absolute inset-[18%] overflow-hidden rounded-full ring-1 ring-linen/25">
                <img alt="Normalizing God podcast studio microphone" className="h-full w-full object-cover" src="/figma/player-art.png" />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>
    </main>
  );
};

const RadioGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M3 10a5 5 0 0 1 10 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <rect x="2.5" y="10" width="3" height="4" rx="1" fill="currentColor" />
    <rect x="10.5" y="10" width="3" height="4" rx="1" fill="currentColor" />
  </svg>
);

const ArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default Home;
